import React, { useEffect, useState } from "react";
import api from "../../../utils/axios";
import { getAlert, useEscapeClose } from "../../../utils/SamuiUtils";

export default function FileModalWHDoc({
  showModal,
  setShowModal,
  WHDoc_Id,
  WHDoc_No,
}) {
  const [files, setFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [hadFilesFromDB, setHadFilesFromDB] = useState(false);

  useEscapeClose(showModal, () => setShowModal(false));
  useEscapeClose(!!previewFile, () => setPreviewFile(null));

  useEffect(() => {
    if (!showModal) {
      setHadFilesFromDB(false);
      return;
    }
    fetchFiles();
  }, [showModal, WHDoc_Id]);

  useEffect(() => {
    if (showModal && files.length === 0 && !hadFilesFromDB) {
      const timer = setTimeout(() => {
        document.getElementById("file-upload-whdoc")?.click();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showModal, files, hadFilesFromDB]);

  const fetchFiles = async () => {
    if (!WHDoc_Id) return;
    try {
      const res = await api.get(`/get-whdoc-files/${WHDoc_Id}`);
      if (res.data.status === "OK" && Array.isArray(res.data.result?.recordset)) {
        const mapped = res.data.result.recordset.map((f) => ({
          File_Id: f.File_Id,
          File_Url: decodeURI(f.FilePath),
          File_Name: f.FileName,
          type: f.FileType || "",
          size: f.FileSize || 0,
        }));
        setFiles(mapped);
        setHadFilesFromDB(mapped.length > 0);
      } else {
        setFiles([]);
        setHadFilesFromDB(false);
      }
    } catch {
      getAlert("FAILED", "โหลดไฟล์ไม่สำเร็จ");
    }
  };

  const handleFileSelection = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) {
      if (files.length === 0) setShowModal(false);
      return;
    }

    const formData = new FormData();
    selected.forEach((f) => formData.append("files", f));
    formData.append("WHDoc_No", WHDoc_No || "");

    try {
      const res = await api.post(`/upload-whdoc-file/${WHDoc_Id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "OK") {
        await fetchFiles();
      } else {
        getAlert("FAILED", res.data.message || "อัปโหลดไม่สำเร็จ");
      }
    } catch {
      getAlert("FAILED", "ไม่สามารถอัปโหลดไฟล์ได้");
    }

    // reset input เพื่อให้เลือกไฟล์เดิมได้อีกครั้ง
    e.target.value = "";
  };

  const handleDelete = async (fileId) => {
    try {
      const res = await api.delete(`/delete-whdoc-file/${fileId}`);
      if (res.data.status === "OK") {
        setFiles((prev) => prev.filter((f) => f.File_Id !== fileId));
      } else {
        getAlert("FAILED", "ลบไฟล์ไม่สำเร็จ");
      }
    } catch {
      getAlert("FAILED", "ไม่สามารถลบไฟล์ได้");
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.File_Url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.File_Name || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      getAlert("FAILED", "ไม่สามารถดาวน์โหลดไฟล์ได้");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[4000] flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full max-w-5xl rounded-lg shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
          <h5 className="modal-title">
            <i className="fa fa-paperclip me-2"></i>
            ไฟล์แนบเอกสารคลัง{WHDoc_No ? ` — ${WHDoc_No}` : ""}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowModal(false)}
          />
        </div>

        {/* Body */}
        <div className="px-4 py-3" style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="mb-0 fw-semibold">
              <i className="fa fa-folder me-2"></i>เอกสารแนบทั้งหมด ({files.length} ไฟล์)
            </h6>
            <>
              <input
                type="file"
                multiple
                id="file-upload-whdoc"
                style={{ display: "none" }}
                onChange={handleFileSelection}
              />
              <button
                className="btn text-white"
                style={{ backgroundColor: "rgb(239,108,0)", borderRadius: 8, padding: "10px 20px" }}
                onClick={() => document.getElementById("file-upload-whdoc").click()}
              >
                <i className="fa fa-plus me-2"></i>แนบไฟล์
              </button>
            </>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 20,
            }}
          >
            {files.length === 0 ? (
              <div className="text-center py-5" style={{ gridColumn: "1 / -1" }}>
                <i className="fa fa-folder-open fa-3x text-muted mb-3"></i>
                <p className="text-secondary">ไม่มีไฟล์แนบ</p>
              </div>
            ) : (
              files.map((file, idx) => (
                <div
                  key={file.File_Id || idx}
                  className="border rounded-lg shadow-sm text-center p-3 position-relative bg-white"
                >
                  {/* ปุ่มลบ */}
                  <button
                    onClick={() => handleDelete(file.File_Id)}
                    className="btn btn-sm btn-danger position-absolute top-0 start-0 m-2 rounded-circle"
                  >
                    <i className="fa fa-times"></i>
                  </button>

                  {/* Preview thumbnail */}
                  <div
                    className="d-flex align-items-center justify-content-center bg-light rounded mb-2"
                    style={{ height: 100, overflow: "hidden", cursor: "pointer" }}
                    onClick={() => setPreviewFile(file)}
                  >
                    {file.type.startsWith("image/") ? (
                      <img
                        src={file.File_Url}
                        alt={file.File_Name}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover", borderRadius: 6 }}
                      />
                    ) : (
                      <i className="fa fa-file" style={{ fontSize: 36, color: "#666" }}></i>
                    )}
                  </div>

                  <div className="fw-semibold small text-truncate mb-1" title={file.File_Name}>
                    {file.File_Name}
                  </div>
                  <div className="text-muted small mb-2">{formatFileSize(file.size)}</div>

                  <div className="d-flex justify-content-center gap-2">
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: "rgb(239,108,0)", color: "white", padding: "4px 8px", fontSize: 11, borderRadius: 6, border: "none" }}
                      onClick={() => setPreviewFile(file)}
                    >
                      <i className="fa fa-eye"></i> ดู
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: "#28a745", color: "white", padding: "4px 8px", fontSize: 11, borderRadius: 6 }}
                      onClick={() => handleDownload(file)}
                    >
                      <i className="fa fa-download"></i> โหลด
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Preview overlay */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[5000]"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
              <h5 className="modal-title">
                <i className="fa fa-eye me-2"></i>ตัวอย่างไฟล์
              </h5>
              <button className="btn-close" onClick={() => setPreviewFile(null)} />
            </div>
            <div className="p-3 text-center">
              {previewFile.type.startsWith("image/") ? (
                <img src={previewFile.File_Url} alt={previewFile.File_Name} className="img-fluid rounded" />
              ) : (
                <iframe src={previewFile.File_Url} width="100%" height="600px" title={previewFile.File_Name} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
